import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { logger } from '@/utils/logger';

export default function ErrorDisplay(): JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();
  let errorMessage: string;
  let errorDetails: string | undefined;

  logger.error('Routing Error:', error); // Log the error for debugging

  if (isRouteErrorResponse(error)) {
    // react-router specific error responses
    errorMessage = error.statusText || 'An unexpected route error occurred.';
    errorDetails = `${error.status} - ${error.data}`;
  } else if (error instanceof Error) {
    errorMessage = error.message || 'An unexpected error occurred.';
    errorDetails = error.stack;
  } else {
    errorMessage = 'An unexpected error occurred.';
    errorDetails = JSON.stringify(error);
  }

  const isDevelopment = import.meta.env.DEV; // Vite uses import.meta.env.DEV

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-destructive">
            An unexpected error occurred
          </CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        {isDevelopment && errorDetails && (
          <CardContent className="mt-4 bg-muted p-4 rounded text-left overflow-auto max-h-60">
            <pre className="text-xs whitespace-pre-wrap">
              <code>{errorDetails}</code>
            </pre>
          </CardContent>
        )}
        <CardFooter className="mt-6 flex justify-center">
          <Button onClick={() => navigate('/', { replace: true })} variant="neutral">
            Go to Dashboard
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
